import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { formatJobNumber } from "@/lib/format";

/**
 * Owning-job link cell shared by the promoted-catalogue table
 * (`/catalogue`, M1 #93) and the drafts table (`/catalogue/drafts`,
 * M1 #84).
 *
 * `jobId` and `jobNumber` arrive aligned — both null when no owning job
 * was recovered, both populated otherwise. The friendly form
 * (`#00000001`) is the canonical render; the UUID is on hover via
 * `title=`. The link target uses the friendly URL so the JobDetail page
 * lands on the canonical form without going through the legacy-UUID
 * redirect step.
 *
 * The empty rendering differs per surface, so it's a prop:
 *   - `"orphaned"` (default) — a draft in flight can legitimately lack
 *     an owning job (the curator died before either attribution signal
 *     landed); the muted "orphaned" badge names that state.
 *   - `"dash"` — a promoted catalogue row with no recoverable owning
 *     job is a legacy / manual-Sheet-edit row, not an error state, so
 *     it renders a plain, non-interactive `—` (M1 #93 AC).
 */
export function OwningJobCell({
  jobId,
  jobNumber,
  emptyVariant = "orphaned",
}: {
  jobId: string | null;
  jobNumber: number | null;
  emptyVariant?: "orphaned" | "dash";
}) {
  if (!jobId || jobNumber === null) {
    return emptyVariant === "dash" ? (
      <span className="text-text-faint">—</span>
    ) : (
      <Badge variant="muted">orphaned</Badge>
    );
  }
  return (
    <Link
      to={`/jobs/${formatJobNumber(jobNumber)}`}
      className="font-mono text-accent hover:underline"
      title={jobId}
    >
      #{formatJobNumber(jobNumber)}
    </Link>
  );
}
